import type { ConfigurationResponse } from '../types';
import type { NetworkProbeResult } from './probeNetwork';
import { getIsNAASupported } from './getIsNAASupported';
import { getIsOnPremises } from './getIsOnPremises';
import { getIsSSOSupported } from './getIsSSOSupported';
import { getUserEmail } from './getUserEmail';
import { maskEmail } from './maskEmail';

// Office exposes no "highest supported set" API, only isSetSupported(name, version).
// Probe known sets in descending order and report the first hit as the highest.
const MAILBOX_REQUIREMENT_SETS = [
  '1.15',
  '1.14',
  '1.13',
  '1.12',
  '1.11',
  '1.10',
  '1.9',
  '1.8',
  '1.7',
  '1.6',
  '1.5',
  '1.4',
  '1.3',
  '1.2',
  '1.1',
];

const safe = <T>(fn: () => T): T | undefined => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

const getHighestMailboxRequirementSet = (): string | undefined =>
  MAILBOX_REQUIREMENT_SETS.find((set) =>
    Office.context.requirements.isSetSupported('Mailbox', set),
  );

let sharedMailbox: boolean | undefined;

export const setSharedMailboxDebug = (value: boolean | undefined) => {
  sharedMailbox = value;
};

export interface DebugInfoExtras {
  config?: ConfigurationResponse['data'];
  network?: NetworkProbeResult;
}

export const getDebugInfo = (extras?: DebugInfoExtras) => {
  const mailboxDiagnostics = safe(() => Office.context.mailbox?.diagnostics);
  const officeDiagnostics = safe(() => Office.context.diagnostics);

  return {
    app: {
      manifestVersion: safe(() =>
        new URLSearchParams(window.location.search).get('manifestVersion'),
      ),
      release: process.env.RELEASE_VERSION,
      environment: process.env.TARGET_ENV,
    },
    outlook: {
      email: safe(() => maskEmail(getUserEmail())),
      hostName: mailboxDiagnostics?.hostName,
      hostVersion: mailboxDiagnostics?.hostVersion,
      platform: Office.context?.platform,
      officeHost: officeDiagnostics?.host,
      officeVersion: officeDiagnostics?.version,
      accountType: Office.context.mailbox?.userProfile?.accountType,
      onPremises: safe(() => getIsOnPremises()),
      isSharedMailbox: sharedMailbox,
      naaSupported: safe(() => getIsNAASupported()),
      ssoSupported: safe(() => getIsSSOSupported()),
      highestMailboxRequirementSet: safe(() => getHighestMailboxRequirementSet()),
      ewsUrl: Office.context.mailbox?.ewsUrl,
      restUrl: safe(() => Office.context.mailbox?.restUrl),
      displayLanguage: Office.context?.displayLanguage,
    },
    config: extras?.config,
    network: extras?.network,
    navigator: {
      userAgent: navigator.userAgent,
      language: navigator.language,
      online: navigator.onLine,
      route: window.location.hash,
    },
  };
};

export const formatDebugInfo = (info: ReturnType<typeof getDebugInfo>): string =>
  JSON.stringify(info, (_key, value) => (value === undefined ? null : value), 2);
